import { defineStore } from 'pinia';
import { adminApi } from '@/api/admin';
import type { SecretSummaryDto } from '@/api/types';

// Per-slot secrets cache used by Monaco completions and diagnostic markers in JsonEditor.
// Lives separately from `useSecretsStore` so editor-driven warmups don't entangle with
// SecretsView's listing state, and so completions can ask for a single slot without
// blowing away the listing view's full catalog. This cache is keyed by slot.
//
// `warmSlot` is idempotent and silent on failure: completion is a best-effort feature, not
// a hard correctness boundary, and a transient API hiccup should not surface as a banner in
// the editor.

interface EditorContextState {
  secretsBySlot: Record<string, SecretSummaryDto[]>;
  loading: Record<string, boolean>;
}

export const useEditorContextStore = defineStore('editorContext', {
  state: (): EditorContextState => ({
    secretsBySlot: {},
    loading: {},
  }),
  actions: {
    async warmSlot(slot: string) {
      if (this.loading[slot] || this.secretsBySlot[slot]) return;
      this.loading[slot] = true;
      try {
        this.secretsBySlot[slot] = await adminApi.listSecrets(slot);
      } catch {
        // Best-effort. Leave the slot uncached so a subsequent edit retries.
      } finally {
        this.loading[slot] = false;
      }
    },
    // Force-refresh the cache for a slot: fetch a fresh list and replace the entry in place.
    // Used after a local secret write/delete and after a remote `secret.write`/`secret.delete`
    // broadcast so completions and the missing-secret marker scanner pick up the new state
    // immediately, without waiting for the next keystroke to trigger a warm.
    async refreshSlot(slot: string) {
      this.loading[slot] = true;
      try {
        this.secretsBySlot[slot] = await adminApi.listSecrets(slot);
      } catch {
        // Best-effort; leave the previous cache in place rather than clearing it.
      } finally {
        this.loading[slot] = false;
      }
    },
    invalidate(slot: string) {
      delete this.secretsBySlot[slot];
    },
  },
});
